import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { doc, serverTimestamp, setDoc } from 'firebase/firestore';
import { FiArrowRight, FiLock, FiMic, FiUnlock } from 'react-icons/fi';
import TopBar from '../components/TopBar';
import DesktopSidebar from '../components/DesktopSidebar';
import MobileNavTabs from '../components/MobileNavTabs';
import desktopLayout from '../components/DesktopSidebarLayout.module.css';
import layoutStyles from '../styles/layout.module.css';
import styles from './FanSettings.module.css';
import { auth, db } from '../firebase';
import { UserContext } from '../App';
import { hasAdminAccess } from '../utils/admin';
import { ANONYMOUS_PHOTO_URL } from '../utils/profileIdentity';
import { hasRoleEntitlement, ROLE_ARTIST } from '../utils/roles';
import { SUPPORTED_CURRENCIES, normalizeCurrency, timezoneLabel } from '../utils/currency';
import { getUserPhotoPath, useStorageUrl } from '../utils/storageMedia';

export default function FanSettings() {
  const appUser = useContext(UserContext);
  const navigate = useNavigate();
  const canAccessAdmin = hasAdminAccess(appUser, null);
  const showArtistUpgrade = !hasRoleEntitlement(appUser, ROLE_ARTIST);
  const hasConnectAccount = Boolean(appUser?.connect?.accountId);
  const lockedConnectCountry = String(appUser?.connect?.country || '').toUpperCase();
  const savedConnectCountry = String(appUser?.settings?.country || '').toUpperCase();
  const detectedCountryCode = String(appUser?.countryCode || '').toUpperCase();
  const [visibility, setVisibility] = useState(appUser?.profileVisibility === 'private' ? 'private' : 'public');
  const [currency, setCurrency] = useState(normalizeCurrency(appUser?.settings?.currency || 'USD'));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const previewPhotoPath = getUserPhotoPath(appUser, 'thumb');
  const previewPhotoUrl = useStorageUrl(previewPhotoPath);

  useEffect(() => {
    setVisibility(appUser?.profileVisibility === 'private' ? 'private' : 'public');
  }, [appUser?.profileVisibility]);

  useEffect(() => {
    setCurrency(normalizeCurrency(appUser?.settings?.currency || 'USD'));
  }, [appUser?.settings?.currency]);

  const handleSave = async () => {
    if (!appUser?.id) return;

    setSaving(true);
    setError('');
    setSuccess('');

    try {
      await setDoc(doc(db, 'users', appUser.id), {
        profileVisibility: visibility,
        settings: {
          ...(appUser?.settings || {}),
          currency,
        },
        updatedAt: serverTimestamp(),
      }, { merge: true });

      setSuccess('Fan settings saved.');
    } catch (err) {
      setError(err?.message || 'Unable to save fan settings.');
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = async () => {
    try {
      setError('');
      setSuccess('');
      await signOut(auth);
      navigate('/signin', { replace: true });
    } catch (err) {
      setError(err?.message || 'Unable to sign out.');
    }
  };

  return (
    <div className={layoutStyles.homeContainer} style={{ paddingBottom: 0 }}>
      <TopBar hideLeft>
        <div className={desktopLayout.mobileTabs}>
          <MobileNavTabs />
        </div>
      </TopBar>
      <div className={`${desktopLayout.shell} ${desktopLayout.shellWithRail}`.trim()}>
        <DesktopSidebar active="settings" note="Control your fan photo, public visibility, and account settings here." />
        <main className={desktopLayout.main}>
          <div className={desktopLayout.mainInner}>
            <div className={styles.pageShell}>
              <header className={styles.heroCard}>
                <div>
                  <span className={styles.eyebrow}>Fan Settings</span>
                  <h1>Fan preferences</h1>
                  <p>Manage your public fan identity, payout region details, and preferred currency used across the app.</p>
                </div>
              </header>

              <section className={styles.grid}>
                <div className={styles.card}>
                  <div className={styles.sectionHeader}>
                    <h2>Profile Preferences</h2>
                    <p>Private fan identities appear as anonymous on public ownership and transaction views. Your currency preference is used when creating collectibles.</p>
                  </div>
                  <div className={styles.visibilityGrid}>
                    <button
                      type="button"
                      className={`${styles.visibilityButton} ${visibility === 'public' ? styles.visibilityButtonActive : ''}`.trim()}
                      onClick={() => setVisibility('public')}
                    >
                      <FiUnlock />
                      <strong>Public</strong>
                      <span>Show your real name and photo on ownership pages.</span>
                    </button>
                    <button
                      type="button"
                      className={`${styles.visibilityButton} ${visibility === 'private' ? styles.visibilityButtonActive : ''}`.trim()}
                      onClick={() => setVisibility('private')}
                    >
                      <FiLock />
                      <strong>Private</strong>
                      <span>Show Anonymous and the default anonymous avatar to other users.</span>
                    </button>
                  </div>
                  <div className={styles.previewRow}>
                    <div className={styles.identityCard}>
                      <img src={visibility === 'private' ? ANONYMOUS_PHOTO_URL : (previewPhotoUrl || ANONYMOUS_PHOTO_URL)} alt="Preview" />
                      <div>
                        <strong>{visibility === 'private' ? 'Anonymous' : [appUser?.firstName, appUser?.lastName].filter(Boolean).join(' ').trim() || appUser?.email || 'Your fan identity'}</strong>
                        <span>{visibility === 'private' ? 'Shown to other users' : 'Visible publicly'}</span>
                      </div>
                    </div>
                  </div>
                  <div className={styles.previewRow}>
                    <label className={styles.sliderLabel} htmlFor="currencySelect">Preferred Currency</label>
                    <select id="currencySelect" value={currency} onChange={(event) => setCurrency(event.target.value)}>
                      {SUPPORTED_CURRENCIES.map((entry) => <option key={entry} value={entry}>{entry}</option>)}
                    </select>
                    <label className={styles.sliderLabel} htmlFor="connectCountrySelect">Stripe Account Country</label>
                    <input
                      id="connectCountrySelect"
                      value={hasConnectAccount ? (lockedConnectCountry || 'Not set') : (savedConnectCountry || 'Choose on Payment page')}
                      readOnly
                      disabled
                    />
                    <p className={styles.state}>
                      {hasConnectAccount
                        ? `Stripe account country is locked to ${lockedConnectCountry || 'the connected account country'}. Disconnect Stripe if you need to recreate the account with another country.`
                        : savedConnectCountry
                          ? `Saved pre-onboarding country: ${savedConnectCountry}. You can change it from the Payment page before creating Stripe Connect.`
                          : detectedCountryCode
                            ? `No Stripe country chosen yet. Open the Payment page and explicitly choose one before creating Stripe Connect. Detected country: ${detectedCountryCode}.`
                            : 'No Stripe country chosen yet. Open the Payment page and explicitly choose one before creating Stripe Connect.'}
                    </p>
                    <p className={styles.state}>Detected timezone: {timezoneLabel(appUser?.settings?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC')}</p>
                    <button type="button" className={styles.saveButton} onClick={handleSave} disabled={saving}>
                      {saving ? 'Saving…' : 'Save Preferences'}
                    </button>
                  </div>
                </div>
              </section>

              {(saving || error || success) ? (
                <section className={styles.card}>
                  {saving ? <p className={styles.state}>Saving fan settings…</p> : null}
                  {error ? <p className={styles.error}>{error}</p> : null}
                  {success ? <p className={styles.success}>{success}</p> : null}
                </section>
              ) : null}

              {showArtistUpgrade ? (
                <section className={styles.card}>
                  <div className={styles.sectionHeader}>
                    <h2>Unlock Creator Tools</h2>
                    <p>Apply for artist approval first, then activate Artist Access from the payment page.</p>
                  </div>
                  <div className={styles.upgradeGrid}>
                    {showArtistUpgrade ? (
                      <button type="button" className={styles.upgradeCard} onClick={() => navigate('/payment')}>
                        <div className={styles.upgradeIcon}><FiMic /></div>
                        <div className={styles.upgradeCopy}>
                          <strong>Apply as an Artist</strong>
                          <p>Submit your artist details for review, then unlock your profile, collectible drops, and creator workspace after approval.</p>
                        </div>
                        <FiArrowRight className={styles.upgradeArrow} />
                      </button>
                    ) : null}
                  </div>
                </section>
              ) : null}

              <section className={styles.card}>
                  <div className={styles.sectionHeader}>
                    <h2>Account Shortcuts</h2>
                    <p>Keep the rest of your account tools reachable from one place without digging through the old settings hub.</p>
                  </div>
                <div className={styles.linkGrid}>
                  {[
                    ['/account', 'Account'],
                    ['/fan/following', 'Following'],
                    ['/transactions', 'Transactions'],
                    ['/payment', 'Stripe Payment'],
                    ['/terms', 'Terms & Purchase Conditions'],
                    ['/services', 'Services'],
                    ...(canAccessAdmin ? [['/admin', 'Admin']] : []),
                  ].map(([to, label]) => (
                    <button key={to} type="button" className={styles.linkButton} onClick={() => navigate(to)}>
                      <span>{label}</span>
                      <FiArrowRight />
                    </button>
                  ))}
                </div>
              </section>

              <div className={styles.footerActions}>
                <button type="button" className={styles.logoutButton} onClick={handleLogout} disabled={saving}>
                  Log Out
                </button>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
